/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, all, select, call, takeLeading, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { ActionRequest, ActionSuccess, Channel } from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import { getChannelFromStorage, setChannelToStorage } from './channels';
import { Selectors as ChannelsSelectors } from './playableChannels';

export enum ActionType {
  INIT = 'channels/tunedChanels/INIT',
  INIT_DONE = 'channels/tunedChanels/INIT_DONE',
  UPDATE = 'channels/tunedChanels/UPDATE',
}

interface InitDonePayload {
  lastChannel: Channel | undefined;
}

type Init = ActionRequest<ActionType.INIT>;
type InitDone = ActionSuccess<ActionType.INIT_DONE, InitDonePayload>;
type Update = ActionSuccess<ActionType.UPDATE, Channel>;

export type Actions = Init | InitDone | Update;

const Actions = {
  init: (): Init => ({
    type: ActionType.INIT,
  }),
  initDone: (lastChannel: Channel | undefined): InitDone => ({
    type: ActionType.INIT_DONE,
    payload: { lastChannel },
  }),
  Update: (payload: Channel): Update => ({ type: ActionType.UPDATE, payload }),
};

export const Creators = {
  init: Actions.init,
  Update: Actions.Update,
};

export interface ReducerState {
  data?: Channel;
}

const initialState: ReducerState = {};

export default function channelsReducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.INIT_DONE:
      return {
        ...state,
        data: action.payload.lastChannel,
      };
    case ActionType.UPDATE:
      return {
        ...state,
        data: action.payload,
      };
    default:
      return state;
  }
}

// SELECTORS

export const Selectors = {
  getLastTunedChannel: (state: StoreState): Channel | undefined => {
    return state.tunedChannels.data;
  },
};

function* initSaga(): SagaIterator {
  let channel: Channel | undefined;
  const storedChannelEpgId: number | undefined = yield call(getChannelFromStorage);

  if (storedChannelEpgId) {
    channel = yield select(ChannelsSelectors.getChannelByEpgId, storedChannelEpgId);
  }

  yield put(Actions.initDone(channel));
}

function* updateSaga(action: Update): SagaIterator {
  yield call(setChannelToStorage, action.payload.epgId);
}

export function* saga(): SagaIterator {
  yield all([takeLeading(ActionType.INIT, initSaga), takeLatest(ActionType.UPDATE, updateSaga)]);
}
